import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { makeStyles } from 'tss-react/mui';
import WaveSurfer from 'wavesurfer.js';

const useStyles = makeStyles()(() => ({
  waveform: {
    width: '100%',
  },
}));

export interface WaveformProps {
    url?: string;
    file?: Blob;
    audioData?: number[];
    duration?: number;
    playing?: boolean;
    onTimeUpdate?: (time: number) => void;
    onDurationChange?: (duration: number) => void;
}

const Waveform: React.FC<WaveformProps> = ({
  url,
  file,
  audioData,
  duration,
  playing,
  onTimeUpdate,
  onDurationChange,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const waveformRef = useRef<HTMLDivElement>(null);
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer>(null);
  const initializationRef = useRef(false);

  // Initialize WaveSurfer
  useEffect(() => {
    if (waveformRef.current && !initializationRef.current) {
      initializationRef.current = true;

      const ws = WaveSurfer.create({
        container: waveformRef.current,
        height: 56,
        waveColor: theme.palette.primary.main,
        progressColor: theme.palette.primary.light,
      });

      setWavesurfer(ws);

      return () => {
        ws.destroy();
        setWavesurfer(null);
        initializationRef.current = false;
      };
    }

    return undefined;
  }, [theme]);

  // Handle event listeners
  useEffect(() => {
    if (wavesurfer) {
      const handleAudioProcess = (time: number) => onTimeUpdate?.(time);
      const handleDecode = (dur: number) => onDurationChange?.(dur);

      wavesurfer.on('audioprocess', handleAudioProcess);
      wavesurfer.on('decode', handleDecode);

      return () => {
        wavesurfer.un('audioprocess', handleAudioProcess);
        wavesurfer.un('decode', handleDecode);
      };
    }

    return undefined;
  }, [wavesurfer, onTimeUpdate, onDurationChange]);

  // Load data
  useEffect(() => {
    if (wavesurfer) {
      if (url) {
        wavesurfer.load(url);
        wavesurfer.toggleInteraction(true);
      } else if (file) {
        wavesurfer.loadBlob(file);
        wavesurfer.toggleInteraction(true);
      } else if (duration && audioData) {
        wavesurfer.load('', [audioData], duration);
        wavesurfer.toggleInteraction(false);
        if (audioData.length === 0) {
          wavesurfer.empty();
        }
      } else {
        wavesurfer.empty();
      }
    }

    return undefined;
  }, [url, wavesurfer, file, audioData, duration]);

  // Control playback
  useEffect(() => {
    if ((url || file) && wavesurfer) {
      if (playing && !wavesurfer.isPlaying()) {
        wavesurfer.play();
      }
      if (!playing && wavesurfer.isPlaying()) {
        wavesurfer.pause();
      }
    }

    return undefined;
  }, [url, file, playing, wavesurfer]);

  return <div ref={waveformRef} className={classes.waveform} />;
};

export default Waveform;
